/**
 * Reading an agenda CSV into agenda items, row by row, before anything is
 * written.
 *
 * Everything here is pure: the file is already parsed (import-csv.ts), and the
 * tracks, people, venues and existing sessions it is matched against are
 * passed in. The panel (AgendaImportPanel.tsx) holds the state and the writes.
 *
 * What a row turns into is exactly what the agenda editor's create form
 * writes: the fields `firestore.rules` accepts on an agenda item create, with
 * the same bounds, and times resolved on the event's clock by the same
 * `resolveWallTime`. `dayKey` and the presenter projection are left to the
 * triggers, as they are for the form.
 */

import {
  resolveWallTime,
  toDate,
} from "../../event-experience/agenda-schedule";
import { timezonePlace } from "../../events/timezones";

import { squashHeader } from "./import-csv";

/** The most data rows one file may carry. See AgendaImportPanel for why. */
export const MAX_AGENDA_IMPORT_ROWS = 1000;

/** Copied from firestore.rules: isValidAgendaItem. */
const MAX_TITLE = 200;
const MAX_DESCRIPTION = 100000;
const MAX_ROOM_NAME = 500;
const MAX_LOCATION = 1000;
const MAX_LOCATION_NOTES = 5000;
const MAX_TRACKS = 50;
const MAX_PRESENTERS = 20;

export type AgendaColumnKey =
  | "title"
  | "date"
  | "startTime"
  | "endTime"
  | "description"
  | "room"
  | "location"
  | "locationNotes"
  | "track"
  | "speakers"
  | "venue";

export interface AgendaColumn {
  key: AgendaColumnKey;
  label: string;
  required: boolean;
  /**
   * Header spellings mapped to this column without asking, compared with
   * case, spaces and punctuation removed. The English label is always one.
   */
  aliases: string[];
}

/**
 * Title, start and end must be mapped. Date may be left out when the start
 * cells carry their own date ("01.10.2026 09:00").
 */
export const AGENDA_COLUMNS: AgendaColumn[] = [
  {
    key: "title",
    label: "Title",
    required: true,
    aliases: ["titel", "session", "name"],
  },
  { key: "date", label: "Date", required: false, aliases: ["datum", "day"] },
  {
    key: "startTime",
    label: "Start time",
    required: true,
    aliases: [
      "start",
      "starts",
      "begin",
      "beginn",
      "von",
      "startzeit",
      "uhrzeitvon",
    ],
  },
  {
    key: "endTime",
    label: "End time",
    required: true,
    aliases: ["end", "ends", "ende", "bis", "endzeit", "uhrzeitbis"],
  },
  {
    key: "description",
    label: "Description",
    required: false,
    aliases: ["beschreibung", "details", "abstract"],
  },
  {
    key: "room",
    label: "Room",
    required: false,
    aliases: ["raum", "roomname"],
  },
  { key: "location", label: "Location", required: false, aliases: ["ort"] },
  {
    key: "locationNotes",
    label: "Location notes",
    required: false,
    aliases: ["wegbeschreibung", "anfahrt"],
  },
  { key: "track", label: "Track", required: false, aliases: ["tracks"] },
  {
    key: "speakers",
    label: "Speakers",
    required: false,
    aliases: [
      "speaker",
      "presenters",
      "presenter",
      "sprecher",
      "referenten",
      "referent",
      "redner",
    ],
  },
  {
    key: "venue",
    label: "Venue",
    required: false,
    aliases: ["veranstaltungsort", "location venue"],
  },
];

export type AgendaMapping = Partial<Record<AgendaColumnKey, string>>;

/** The obvious mapping for these headers: each column takes the first header matching its label or an alias. */
export function guessAgendaMapping(headers: string[]): AgendaMapping {
  const mapping: AgendaMapping = {};
  const taken = new Set<string>();
  for (const column of AGENDA_COLUMNS) {
    const names = new Set(
      [column.label, column.key, ...column.aliases].map(squashHeader),
    );
    const match = headers.find(
      (header) => !taken.has(header) && names.has(squashHeader(header)),
    );
    if (match) {
      mapping[column.key] = match;
      taken.add(match);
    }
  }
  return mapping;
}

/** What is wrong with a mapping, or null when it can be used. */
export function mappingProblem(mapping: AgendaMapping): string | null {
  for (const column of AGENDA_COLUMNS) {
    if (column.required && !mapping[column.key])
      return `Choose the column that holds ${column.label}.`;
  }
  const chosen = Object.values(mapping).filter(Boolean);
  if (new Set(chosen).size !== chosen.length)
    return "Each CSV column can only be used once.";
  return null;
}

/** One row's cells, by agenda column. */
export type AgendaRowValues = Record<AgendaColumnKey, string>;

export function mapRowValues(
  values: Record<string, string>,
  mapping: AgendaMapping,
): AgendaRowValues {
  const out = {} as AgendaRowValues;
  for (const column of AGENDA_COLUMNS) {
    const header = mapping[column.key];
    out[column.key] = header ? (values[header] ?? "").trim() : "";
  }
  return out;
}

// ── Dates and times ─────────────────────────────────────────────────────────

const ISO_DATE = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const GERMAN_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const CLOCK = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

/**
 * "2026-10-01" or "1.10.2026" as "2026-10-01", or null.
 *
 * Two formats and no more. YYYY-MM-DD is what any tool writes when asked for
 * a date without a locale. DD.MM.YYYY is what German Excel shows and exports.
 * Slashed dates are refused because 01/10/2026 is the first of October in
 * Germany and the tenth of January in the US, and guessing moves a session by
 * months without a word.
 */
export function parseImportDate(text: string): string | null {
  const value = text.trim();
  let year: number;
  let month: number;
  let day: number;
  const iso = ISO_DATE.exec(value);
  const german = GERMAN_DATE.exec(value);
  if (iso) {
    [year, month, day] = iso.slice(1).map(Number);
  } else if (german) {
    [day, month, year] = german.slice(1).map(Number);
  } else {
    return null;
  }
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  )
    return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * "9:30", "09:30" or "09:30:00" as "09:30", or null.
 *
 * Seconds are accepted because Excel writes them when a cell is formatted as a
 * time with seconds, but only ":00": an agenda is on the minute, and dropping
 * a real 09:30:45 would be a silent change.
 */
export function parseImportTime(text: string): string | null {
  const match = CLOCK.exec(text.trim());
  if (!match) return null;
  const [hour, minute] = [Number(match[1]), Number(match[2])];
  if (hour > 23 || minute > 59) return null;
  if (match[3] !== undefined && match[3] !== "00") return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/**
 * A time cell, which may carry its own date: "09:30", "01.10.2026 09:30" or
 * "2026-10-01T09:30". `date` is null when the cell has none.
 */
export function parseTimeCell(
  text: string,
): { date: string | null; time: string } | null {
  const value = text.trim();
  const split = /^(\S+)[ T]+(\S+)$/.exec(value);
  if (split) {
    const date = parseImportDate(split[1]);
    const time = parseImportTime(split[2]);
    return date && time ? { date, time } : null;
  }
  const time = parseImportTime(value);
  return time ? { date: null, time } : null;
}

// ── Matching names to documents ─────────────────────────────────────────────

/** Names compare with case, accents' composition and runs of spaces ignored. */
export function normaliseName(text: string): string {
  return text
    .normalize("NFC")
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("de");
}

/**
 * Several names in one cell. Semicolons, vertical bars and line breaks
 * separate them; commas do not, because "Müller, Anna" is one person.
 */
export function splitList(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(/[;|\n\r]+/)) {
    const value = part.trim();
    if (!value || seen.has(normaliseName(value))) continue;
    seen.add(normaliseName(value));
    out.push(value);
  }
  return out;
}

/**
 * Somebody a session can name as a presenter, under the id the agenda editor
 * would write for them. D14: that is an attendee or a speaker. A speaker who
 * is the same human as an attendee (`attendeeId`) is written under the
 * attendee's id, as the editor's chip list does, and their speaker name is
 * one more name the attendee can be found by.
 */
export interface PresenterCandidate {
  id: string;
  label: string;
  names: string[];
  emails: string[];
  /** Left the event: named in the problem, never matched. */
  withdrawn: boolean;
}

export function presenterCandidates(
  attendees: { id: string; name: string; email?: string; status?: string }[],
  speakers: { id: string; name: string; attendeeId?: string }[],
): PresenterCandidate[] {
  const byId = new Map<string, PresenterCandidate>();
  for (const attendee of attendees) {
    byId.set(attendee.id, {
      id: attendee.id,
      label: attendee.name,
      names: [attendee.name],
      emails: attendee.email ? [attendee.email] : [],
      withdrawn: attendee.status === "withdrawn",
    });
  }
  for (const speaker of speakers) {
    const linked = speaker.attendeeId
      ? byId.get(speaker.attendeeId)
      : undefined;
    if (linked) {
      linked.names.push(speaker.name);
    } else {
      byId.set(speaker.id, {
        id: speaker.id,
        label: speaker.name,
        names: [speaker.name],
        emails: [],
        withdrawn: false,
      });
    }
  }
  return [...byId.values()];
}

type Match<T> =
  | { kind: "one"; value: T }
  | { kind: "none" }
  | { kind: "many"; count: number };

function matchByName<T extends { id: string; name: string }>(
  wanted: string,
  options: T[],
): Match<T> {
  const key = normaliseName(wanted);
  const hits = options.filter((option) => normaliseName(option.name) === key);
  if (hits.length === 1) return { kind: "one", value: hits[0] };
  if (hits.length === 0) return { kind: "none" };
  return { kind: "many", count: hits.length };
}

function matchPresenter(
  wanted: string,
  people: PresenterCandidate[],
): Match<PresenterCandidate> {
  const key = normaliseName(wanted);
  const byEmail = wanted.includes("@");
  const hits = people.filter((person) =>
    byEmail
      ? person.emails.some((email) => normaliseName(email) === key)
      : person.names.some((name) => normaliseName(name) === key),
  );
  if (hits.length === 1) return { kind: "one", value: hits[0] };
  if (hits.length === 0) return { kind: "none" };
  return { kind: "many", count: hits.length };
}

// ── Validation ─────────────────────────────────────────────────────────────

export interface AgendaImportContext {
  /** The event's zone, already checked with `usableTimeZone`. */
  timeZone: string;
  tracks: { id: string; name: string }[];
  people: PresenterCandidate[];
  venues: { id: string; name: string }[];
  /** The sessions already on the agenda, for the duplicate check. */
  existing: { id: string; title: string; startsAt?: unknown }[];
}

/** The fields the create writes, less `createdAt`, which is the server's. */
export interface AgendaItemDraft {
  title: string;
  description?: string;
  roomName?: string;
  location?: string;
  locationNotes?: string;
  startsAt: Date;
  endsAt: Date;
  trackIds: string[];
  presenterIds: string[];
}

export interface AgendaRowCheck {
  /** Set when there are no problems. A duplicate still has one. */
  draft: AgendaItemDraft | null;
  /** Reasons the row cannot be written as it is. */
  problems: string[];
  /** Things the row will be written with anyway, said before it is. */
  notes: string[];
  /**
   * Speakers the row names that are neither an attendee nor a speaker of the
   * event, as written in the file. The session is imported without them,
   * which `notes` says, and the panel lists each name once with its rows.
   */
  unknownSpeakers: string[];
  /** Why this looks like a session that already exists, if it does. */
  duplicate: string | null;
}

/**
 * One row, checked alone. Duplicates need the other rows too, so
 * `checkAgendaRows` adds them.
 */
export function checkAgendaRow(
  row: AgendaRowValues,
  context: AgendaImportContext,
): AgendaRowCheck {
  const problems: string[] = [];
  const notes: string[] = [];

  // One line: the agenda form's title field cannot hold a line break, and a
  // spreadsheet cell can.
  const title = row.title.replace(/\s*[\r\n]+\s*/g, " ").trim();
  if (!title) problems.push("The title is empty.");
  else if (title.length > MAX_TITLE)
    problems.push(`The title is longer than ${MAX_TITLE} characters.`);

  const times = checkTimes(row, context.timeZone, problems, notes);

  const trackIds: string[] = [];
  for (const name of splitList(row.track)) {
    const match = matchByName(name, context.tracks);
    if (match.kind === "one") {
      if (!trackIds.includes(match.value.id)) trackIds.push(match.value.id);
    } else if (match.kind === "none") {
      problems.push(
        `No track is called “${name}”. Add it on the Tracks tab, or correct the name.`,
      );
    } else {
      problems.push(
        `${match.count} tracks are called “${name}”. Rename one on the Tracks tab so each name is used once.`,
      );
    }
  }
  if (trackIds.length > MAX_TRACKS)
    problems.push(`A session can have at most ${MAX_TRACKS} tracks.`);

  const presenterIds: string[] = [];
  const unknownSpeakers: string[] = [];
  for (const name of splitList(row.speakers)) {
    const match = matchPresenter(name, context.people);
    if (match.kind === "one") {
      if (match.value.withdrawn) {
        problems.push(`${match.value.label} withdrew from the event.`);
      } else if (!presenterIds.includes(match.value.id)) {
        presenterIds.push(match.value.id);
      }
    } else if (match.kind === "none") {
      // Not a problem: the session does not depend on its speakers existing,
      // and holding it back would lose a whole session for one name. It is
      // imported without them, and the name is reported for the organiser to
      // add on the Speakers tab.
      unknownSpeakers.push(name);
      notes.push(
        name.includes("@")
          ? `No attendee has the email ${name}, so the session is imported without them.`
          : `Nobody called “${name}” is an attendee or a speaker of this event, so the session is imported without them.`,
      );
    } else {
      problems.push(
        `${match.count} people are called “${name}”. Use their email instead.`,
      );
    }
  }
  if (presenterIds.length > MAX_PRESENTERS)
    problems.push(`A session can have at most ${MAX_PRESENTERS} speakers.`);

  let location = row.location.trim();
  const venueName = row.venue.trim();
  if (venueName) {
    const match = matchByName(venueName, context.venues);
    if (match.kind === "one") {
      // D16 took the venue link off the agenda item; the free-text location
      // is where a session says where it is. The venue's own spelling goes
      // first, so "Main Hall, East wing" reads from large to small.
      location = location
        ? `${match.value.name}, ${location}`
        : match.value.name;
    } else if (match.kind === "none") {
      problems.push(
        `No venue is called “${venueName}”. Add it on the Venues tab, or correct the name.`,
      );
    } else {
      problems.push(
        `${match.count} venues are called “${venueName}”. Rename one on the Venues tab so each name is used once.`,
      );
    }
  }

  const description = row.description.trim();
  const roomName = row.room.trim();
  const locationNotes = row.locationNotes.trim();
  for (const [value, max, label] of [
    [description, MAX_DESCRIPTION, "The description"],
    [roomName, MAX_ROOM_NAME, "The room"],
    [
      location,
      MAX_LOCATION,
      venueName ? "The venue and location together" : "The location",
    ],
    [locationNotes, MAX_LOCATION_NOTES, "The location notes"],
  ] as const) {
    if (value.length > max)
      problems.push(`${label} is longer than ${max} characters.`);
  }

  const draft: AgendaItemDraft | null =
    problems.length === 0 && times
      ? {
          title,
          // Blank is left out rather than written as "", as the form does.
          ...(description ? { description } : {}),
          ...(roomName ? { roomName } : {}),
          ...(location ? { location } : {}),
          ...(locationNotes ? { locationNotes } : {}),
          startsAt: times.start,
          endsAt: times.end,
          trackIds,
          presenterIds,
        }
      : null;
  return { draft, problems, notes, unknownSpeakers, duplicate: null };
}

function checkTimes(
  row: AgendaRowValues,
  timeZone: string,
  problems: string[],
  notes: string[],
): { start: Date; end: Date } | null {
  const before = problems.length;
  let date: string | null = null;
  if (row.date.trim()) {
    date = parseImportDate(row.date);
    if (!date)
      problems.push(
        `“${row.date.trim()}” is not a date. Write it as 2026-10-01 or 01.10.2026.`,
      );
  }
  const start = row.startTime.trim() ? parseTimeCell(row.startTime) : null;
  const end = row.endTime.trim() ? parseTimeCell(row.endTime) : null;
  if (!row.startTime.trim()) problems.push("The start time is empty.");
  else if (!start)
    problems.push(
      `“${row.startTime.trim()}” is not a time. Write it as 09:30.`,
    );
  if (!row.endTime.trim()) problems.push("The end time is empty.");
  else if (!end)
    problems.push(`“${row.endTime.trim()}” is not a time. Write it as 10:15.`);
  if (problems.length > before || !start || !end) return null;

  if (start.date && date && start.date !== date) {
    problems.push(
      `The date column says ${date} and the start time says ${start.date}.`,
    );
    return null;
  }
  const startDate = start.date ?? date;
  if (!startDate) {
    problems.push("The date is empty.");
    return null;
  }
  // An end with no date of its own is on the start's day. A session past
  // midnight writes its end date into the end cell.
  const endDate = end.date ?? startDate;

  const resolved: Date[] = [];
  let repeated: string | null = null;
  for (const wall of [`${startDate}T${start.time}`, `${endDate}T${end.time}`]) {
    const result = resolveWallTime(wall, timeZone);
    if (result.kind === "skipped") {
      problems.push(
        `${describeWall(wall)} does not exist in ${clockName(timeZone)}, because the clocks go forward that night. Choose a time after the change.`,
      );
      return null;
    }
    if (!("instant" in result)) {
      problems.push(`${describeWall(wall)} is not a real time.`);
      return null;
    }
    if (result.kind === "repeated") repeated ??= wall;
    if (result.kind === "repeated")
      notes.push(
        `${describeWall(wall)} happens twice in ${clockName(timeZone)}, because the clocks go back that night. The first one, before the change, is used.`,
      );
    resolved.push(result.instant);
  }
  const [startsAt, endsAt] = resolved;
  if (endsAt <= startsAt) {
    if (repeated)
      // The night the clocks go back: both times read on the first pass, so
      // a session crossing the change ends before it starts. An end date
      // would not help, which the midnight advice below would suggest.
      problems.push(
        `The end time must be after the start time. ${describeWall(repeated)} happens twice in ${clockName(timeZone)} and the first one is used, which puts this end before the start. Move the session out of the repeated hour.`,
      );
    else if (!end.date && end.time < start.time)
      problems.push(
        "The end time must be after the start time. For a session past midnight, put the end date in the end time cell too, as 02.10.2026 01:00.",
      );
    else problems.push("The end time must be after the start time.");
    return null;
  }
  const hours = (endsAt.getTime() - startsAt.getTime()) / 3_600_000;
  if (hours > 24)
    notes.push(
      `This session runs for ${Math.round(hours)} hours. Check the end date if that is not intended.`,
    );
  return { start: startsAt, end: endsAt };
}

/** "02:30 on 2026-03-29", as the agenda form says it. */
function describeWall(wall: string): string {
  const [date, time] = wall.split("T");
  return `${time} on ${date}`;
}

function clockName(timeZone: string): string {
  return `${timezonePlace(timeZone)} time`;
}

function duplicateKey(title: string, startsAt: Date): string {
  return `${normaliseName(title)}|${startsAt.getTime()}`;
}

/**
 * Every row checked, and duplicates marked.
 *
 * A row is a duplicate when a session with the same title (compared as names
 * are) starts at the same instant, either on the agenda already or on an
 * earlier row of this file. Only the later row is marked, so importing a file
 * twice marks every row of the second run, and one repeated line in a file
 * marks its second copy.
 *
 * `skip` holds rows left out of the check: dismissed rows, which cannot clash
 * with anything, and rows already written, which would otherwise match the
 * session they just became. `writtenIds` are those sessions' ids, so the
 * agenda, reloaded after a write, does not count them either. `written`
 * names the written rows again: they get no check of their own, but every
 * other row is compared with them, so a failed row retried after a later
 * copy of it landed is still caught.
 */
export function checkAgendaRows(
  rows: { rowNumber: number; values: AgendaRowValues; overflow?: boolean }[],
  context: AgendaImportContext,
  skip: Set<number> = new Set(),
  writtenIds: Set<string> = new Set(),
  written: Set<number> = new Set(),
): Map<number, AgendaRowCheck> {
  const existing = new Map<string, string>();
  for (const item of context.existing) {
    if (writtenIds.has(item.id)) continue;
    const startsAt = toDate(item.startsAt);
    if (startsAt) existing.set(duplicateKey(item.title, startsAt), item.title);
  }
  const earlier = new Map<string, number>();
  const landed = new Map<string, number>();
  for (const row of rows) {
    if (!written.has(row.rowNumber)) continue;
    const draft = checkAgendaRow(row.values, context).draft;
    if (draft)
      landed.set(duplicateKey(draft.title, draft.startsAt), row.rowNumber);
  }
  const checks = new Map<number, AgendaRowCheck>();
  for (const row of rows) {
    if (skip.has(row.rowNumber)) continue;
    const check = checkAgendaRow(row.values, context);
    if (row.overflow) {
      check.problems.unshift(
        "This row has more cells than the header row has columns. A separator inside a cell needs quotes around the cell.",
      );
      check.draft = null;
    }
    if (check.draft) {
      const key = duplicateKey(check.draft.title, check.draft.startsAt);
      const onAgenda = existing.get(key);
      const inFile = earlier.get(key);
      const imported = landed.get(key);
      if (onAgenda !== undefined)
        check.duplicate = `“${onAgenda}” is already on the agenda at this time.`;
      else if (imported !== undefined)
        check.duplicate = `Row ${imported} has the same title and start time, and is imported already.`;
      else if (inFile !== undefined)
        check.duplicate = `Row ${inFile} has the same title and start time.`;
      else earlier.set(key, row.rowNumber);
    }
    checks.set(row.rowNumber, check);
  }
  return checks;
}

/** One speaker name the file uses that the event has no one for. */
export interface UnknownSpeaker {
  /** As the first row wrote it. */
  name: string;
  rows: number[];
}

/**
 * The unknown speakers across rows, each name once (compared as names are,
 * emails included) with the rows it appears in, in the order first met.
 */
export function collectUnknownSpeakers(
  entries: { rowNumber: number; unknownSpeakers: string[] }[],
): UnknownSpeaker[] {
  const byName = new Map<string, UnknownSpeaker>();
  for (const entry of entries) {
    for (const name of entry.unknownSpeakers) {
      const key = normaliseName(name);
      const found = byName.get(key);
      if (!found) byName.set(key, { name, rows: [entry.rowNumber] });
      else if (!found.rows.includes(entry.rowNumber))
        found.rows.push(entry.rowNumber);
    }
  }
  return [...byName.values()];
}

// ── Template ────────────────────────────────────────────────────────────────

export const AGENDA_TEMPLATE_FILE_NAME = "agenda-template.csv";

/**
 * The downloadable template: every column under its English label, which the
 * mapping step picks up by itself, and two example rows showing both date
 * formats, a list of speakers and a session past midnight.
 *
 * Semicolons and a byte-order mark, because that is what German Excel opens
 * straight into columns with its umlauts intact, and it is also what it writes
 * back when the organiser saves as "CSV UTF-8".
 */
export function agendaTemplateCsv(): string {
  const rows = [
    AGENDA_COLUMNS.map((column) => column.label),
    [
      "Opening keynote",
      "2026-10-01",
      "09:00",
      "09:45",
      "Welcome and the plan for the day.",
      "Auditorium",
      "Ground floor",
      "",
      "Main Programme",
      "Ada Lovelace; grace@example.com",
      "Main Hall",
    ],
    [
      "Late session",
      "01.10.2026",
      "23:00",
      "02.10.2026 00:30",
      "",
      "Room 2",
      "",
      "",
      "",
      "",
      "",
    ],
  ];
  return `\uFEFF${rows.map((row) => row.map(csvCell).join(";")).join("\r\n")}\r\n`;
}

function csvCell(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
